import { type ReactNode } from "react";
import { Link } from "react-router-dom";

import { type ICreateProjectParams } from "../api/WebApiClient.js";
import {
  type BlastJob,
  type Group,
  type InputPlaybookInput,
  type Parent,
  type Project,
  type UserGroup,
} from "../graphql.js";
import { SelMessage } from "../messages/SelMessage.js";
import { pluralizeWord } from "./string.js";

export function buildSavedViewMessage(viewName: string, project: Project, isUpdate?: boolean) {
  return SelMessage({
    id: isUpdate ? "view.update.successMessage" : "view.save.successMessage",
    values: {
      viewName: viewName,
      projectName: project.name,
      link: (chunk) => <Link to={`/project/${project.id}/savedItems/view/view`}>{chunk}</Link>,
    },
  });
}

export function buildSavedGroupMessage(group: Group, project: Project) {
  return SelMessage({
    id: "group.save.successMessage",
    values: {
      groupName: group.name,
      groupLink: (chunk) => <Link to={`/group/${group.id}`}>{chunk}</Link>,
      projectName: project.name,
      projectLink: (chunk) => <Link to={`/project/${project.id}`}>{chunk}</Link>,
    },
  });
}

export function buildSavedParentMessage(parent: Parent) {
  return SelMessage({
    id: "playbook.submission.sampleContext.successMessage",
    values: {
      parentName: parent.name,
      link: () => <Link to={`/parent/${parent.id}`}>{parent.name}</Link>,
    },
  });
}

export function buildCopyGroupsMessage(count: number, groupType: string, project: Project) {
  return SelMessage({
    id: "group.copy.successMessage",
    values: {
      groupsCount: count,
      groupType: groupType,
      projectName: project.name,
      link: (chunk) => <Link to={`/project/${project.id}`}>{chunk}</Link>,
    },
  });
}

export function buildCopyViewsMessage(count: number, project: Project) {
  return SelMessage({
    id: "view.copy.successMessage",
    values: {
      viewsCount: count,
      projectName: project.name,
      link: (chunk) => <Link to={`/project/${project.id}`}>{chunk}</Link>,
    },
  });
}

export function buildCopyFlagsMessage(count: number, project: Project) {
  return SelMessage({
    id: "flag.copy.successMessage",
    values: {
      flagsCount: count,
      projectName: project.name,
      link: (chunk) => <Link to={`/project/${project.id}`}>{chunk}</Link>,
    },
  });
}

export function buildDeleteActivityMessage(activityType: string, activityIds: number[]) {
  return SelMessage({
    id: "activities.delete.successMessage",
    values: {
      activitiesCount: activityIds.length,
      activityType: pluralizeWord(activityType, activityIds.length),
    },
  });
}

export function buildCreateProjectMessage(payload: ICreateProjectParams, project: Project) {
  let message: ReactNode;

  if (payload.isPublic) {
    message = SelMessage({
      id: "projects.create.public.successMessage",
      values: {
        projectName: project.name,
        link: (chunk) => <Link to={`/project/${project.id}`}>{chunk}</Link>,
      },
    });
  } else if (payload.userNames.length === 0) {
    message = SelMessage({
      id: "projects.create.private.successMessage",
      values: {
        projectName: project.name,
        link: (chunk) => <Link to={`/project/${project.id}`}>{chunk}</Link>,
      },
    });
  } else {
    message = SelMessage({
      id: "projects.create.open.successMessage",
      values: {
        membersCount: payload.userNames.length,
        projectName: project.name,
        link: (chunk) => <Link to={`/project/${project.id}`}>{chunk}</Link>,
      },
    });
  }

  return message;
}

export function buildCreateUsergroupMessage(usergroup: UserGroup, trackerLicensed: boolean) {
  return SelMessage({
    id: "admin.userGroup.created.successMessage",
    values: {
      usergroupName: usergroup.name,
      userCount: usergroup.members?.length || 0,
      trackerLicensed,
      sampleTypesCount: usergroup.sampleTypes?.length || 0,
      genomesCount: usergroup.organisms?.length || 0,
    },
  });
}

export function buildModifyProjectMessage(project: Project) {
  return SelMessage({
    id: "projects.modify.successMessage",
    values: {
      projectName: project.name,
      link: (chunk) => <Link to={`/project/${project.id}`}>{chunk}</Link>,
    },
  });
}

export function buildBlastSubmitMessage(job: BlastJob) {
  return SelMessage({
    id: "job.submission.successMessage",
    values: {
      link: (chunk) => <Link to={`/job/${job.id}`}>{chunk}</Link>,
    },
  });
}

export function buildSavedPlaybookMessage(playbookPayload: InputPlaybookInput) {
  return SelMessage({
    id: "admin.playbook.saved.successMessage",
    values: {
      playbookName: playbookPayload.name,
      editMode: !playbookPayload.playbookId,
      link: (msg) => <Link to="/admin/roles">{msg}</Link>,
    },
  });
}

export function buildSavedFlagMessage(flagName: string, callback: () => void) {
  return SelMessage({
    id: "flag.saved.successMessage",
    values: {
      flagName: flagName,
      link: (msg) => <a onClick={callback}>{msg}</a>,
    },
  });
}
